import React, { useRef } from 'react';
import PropTypes from 'prop-types';
import { Box, Text, Icon } from 'components/ui';
import { colors, radii, borders } from 'style/config';
import { wrapFunctionalComponent } from 'utils/framework';
import Markdown from 'components/Markdown/Markdown';
import helpersUtils from 'utils/Helpers';

const { replaceDoubleAsterisks } = helpersUtils;

const styles = {
    root: {
        appearance: 'none',
        textAlign: 'left',
        transition: 'border-color 150ms ease',
        // SHP-5462: `overflow-wrap: break-word` breaks a long word visually but leaves the box's
        // intrinsic min-content sized to it, so a one-word store name such as GAITHERSBURG widened
        // the Pickup card past its 1/3 share at 375px. `anywhere` shrinks min-content too. It has
        // to reach the descendants — Text declares `break-word` on its own element, which beats an
        // inherited value — hence the doubled class, which outranks Text's single-class rule.
        '&&, && *': {
            overflowWrap: 'anywhere'
        },
        ':focus-visible': {
            outline: `2px solid ${colors.black}`,
            outlineOffset: 2
        }
    },
    enabled: {
        cursor: 'pointer',
        background: colors.white
    },
    disabled: {
        cursor: 'default',
        background: colors.lightGray
    },
    subtitleMarkdown: {
        '& > :first-child': {
            display: 'inline'
        }
    }
};

/**
 * Resolves the glyph a card paints (SHP-5451). A selected card swaps to its filled
 * `activeIconName`, but `disabled` takes priority the same way it does for the border
 * and the icon color: the fallback effect in FulfillmentMethods runs after commit, so a
 * card can paint one frame as both selected and disabled.
 */
const resolveIconName = ({ iconName, activeIconName, isSelected, disabled }) =>
    isSelected && !disabled && activeIconName ? activeIconName : iconName;

/**
 * SHP-5464: only the selected, enabled card keeps the inherited black — every other card
 * dims its icon to gray. Mirrors the `disabled` precedence in `resolveIconName`.
 */
const resolveIconColor = ({ isSelected, disabled }) => (isSelected && !disabled ? undefined : 'gray');

/**
 * FulfillmentCard
 *
 * A single selectable card in the PDP Fulfillment Methods radiogroup (SHP-5111).
 * Renders as a native `<button role="radio">` by default. When `interactive` is
 * true (SHP-5258), it renders as a keyboard-operable `<div role="radio">` instead,
 * since a native `<button>` cannot legally contain another interactive element —
 * this variant is used when `titleContent` embeds a real link (e.g. "Sign in").
 * SHP-5451 adds `activeIconName` so a selected card can swap to a filled glyph.
 *
 * @param {object} props
 * @param {string} [props.id] - DOM id for the card.
 * @param {string} props.iconName - Icon name rendered above the title.
 * @param {string} [props.activeIconName] - Filled icon variant rendered in place of `iconName` while selected and enabled. Cards that omit it keep `iconName` in every state.
 * @param {string} [props.title] - Plain-text title; ignored when `titleContent` is provided.
 * @param {React.ReactNode} [props.titleContent] - Rich title content, overrides `title`.
 * @param {string} [props.etaText] - Green ETA text shown below the title.
 * @param {string} [props.etaColor] - Color token for `etaText`/`secondaryText`.
 * @param {string} [props.secondaryText] - Overrides `etaText` (e.g. "Not available for this item").
 * @param {boolean} [props.disabled] - Renders the card as non-interactive/informational.
 * @param {boolean} [props.isSelected] - Whether this card is the selected radio option.
 * @param {Function} props.onSelect - Called when the card is activated (click, Enter, or Space).
 * @param {string} [props.ariaLabel] - Accessible name for the radio.
 * @param {boolean} [props.interactive] - Renders as `<div role="radio" tabIndex={0}>` to allow nested interactive content.
 * @param {boolean} [props.enableMarkdownSubtitle] - Renders `secondaryText`/`etaText` through Markdown (bold segments via `**`).
 * @returns {JSX.Element}
 */
const FulfillmentCard = ({
    id,
    iconName,
    activeIconName,
    title,
    titleContent,
    etaText,
    etaColor = 'green',
    secondaryText,
    disabled,
    isSelected,
    onSelect,
    ariaLabel,
    interactive,
    enableMarkdownSubtitle
}) => {
    const borderColor = disabled ? colors.lightGray : isSelected ? colors.black : colors.midGray;
    const borderWidth = isSelected ? borders[2] : borders[1];
    const textColor = disabled ? 'gray' : 'black';
    const secondaryColor = disabled ? 'gray' : etaColor;
    const subtitleContent = secondaryText || etaText;
    const resolvedIconName = resolveIconName({
        iconName,
        activeIconName,
        isSelected,
        disabled
    });
    const iconColor = resolveIconColor({ isSelected, disabled });
    const rootRef = useRef(null);

    // SHP-5258: the signed-out Free Shipping card nests a real "Sign in" link inside
    // its title. A native <button> can't legally contain another interactive element,
    // so `interactive` swaps the root to a keyboard-operable <div role="radio"> instead.
    const handleKeyActivate = e => {
        if (!disabled && (e.key === 'Enter' || e.key === ' ')) {
            e.preventDefault();
            onSelect();
        }
    };

    // SHP-5538: VoiceOver activates a card through the accessibility press action, which
    // fires a click without moving DOM focus, so once the card re-rendered as selected the
    // VoiceOver cursor snapped back to whatever was really focused — a shade swatch —
    // instead of the card. The legacy layout got this free from a real <input type="radio">;
    // the ARIA radiogroup pattern wants focus to follow selection, so place it here.
    const handleActivate = () => {
        rootRef.current?.focus();
        onSelect();
    };

    return (
        <Box
            ref={rootRef}
            is={interactive ? 'div' : 'button'}
            type={interactive ? undefined : 'button'}
            id={id}
            role='radio'
            tabIndex={interactive && !disabled ? 0 : undefined}
            aria-checked={isSelected}
            aria-disabled={disabled || undefined}
            aria-label={ariaLabel}
            onClick={disabled ? undefined : handleActivate}
            onKeyDown={interactive ? handleKeyActivate : undefined}
            paddingX={3}
            paddingY={3}
            borderRadius={radii[1]}
            width='100%'
            minWidth={0}
            height='100%'
            display='flex'
            flexDirection='column'
            justifyContent='flex-start'
            alignItems='flex-start'
            css={[
                styles.root,
                disabled ? styles.disabled : styles.enabled,
                {
                    gap: 4,
                    border: `${borderWidth} ${borderColor}`
                }
            ]}
        >
            <Icon
                name={resolvedIconName}
                size={18}
                color={iconColor}
            />
            {titleContent || (
                <Text
                    is='p'
                    fontSize='base'
                    fontWeight='bold'
                    color={textColor}
                    lineHeight='tight'
                    children={title}
                />
            )}
            <Text
                is='p'
                fontSize='sm'
                color={secondaryColor}
                lineHeight='tight'
            >
                {enableMarkdownSubtitle ? (
                    <Markdown
                        is='span'
                        css={styles.subtitleMarkdown}
                        content={replaceDoubleAsterisks(subtitleContent)}
                    />
                ) : (
                    subtitleContent
                )}
            </Text>
        </Box>
    );
};

FulfillmentCard.propTypes = {
    id: PropTypes.string,
    iconName: PropTypes.string.isRequired,
    activeIconName: PropTypes.string,
    title: PropTypes.string,
    titleContent: PropTypes.node,
    etaText: PropTypes.string,
    etaColor: PropTypes.string,
    secondaryText: PropTypes.string,
    disabled: PropTypes.bool,
    isSelected: PropTypes.bool,
    onSelect: PropTypes.func.isRequired,
    ariaLabel: PropTypes.string,
    interactive: PropTypes.bool,
    enableMarkdownSubtitle: PropTypes.bool
};

export default wrapFunctionalComponent(FulfillmentCard, 'FulfillmentCard');
